import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Stack,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tab,
  Tabs,
  Fade,
  Zoom,
  Grow,
  Slide,
  createTheme,
  ThemeProvider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Rating
} from '@mui/material';
import {
  TouchApp as TouchIcon,
  Smartphone as MobileIcon,
  QrCode as QrIcon,
  Security as SecurityIcon,
  School as TrainingIcon,
  Translate as LanguageIcon,
  CameraAlt as CameraIcon,
  Print as PrintIcon,
  Scanner as ScannerIcon,
  VolumeUp as AudioIcon,
  ArrowForward as ArrowIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as CheckIcon,
  Language as GlobeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Assignment as FormIcon,
  Timeline as TimelineIcon,
  Lock as LockIcon,
  Fingerprint as FingerprintIcon,
  VerifiedUser as VerifiedIcon,
  Shield as ShieldIcon,
  VpnKey as KeyIcon,
  History as HistoryIcon,
  GTranslate as TranslateIcon,
  RecordVoiceOver as VoiceIcon,
  ClosedCaption as CaptionIcon,
  MenuBook as BookIcon
} from '@mui/icons-material';
import HomeFooter from '../HomeFooter.jsx';
import { motion, AnimatePresence, useInView, useAnimation } from 'framer-motion';
import Navbar from "../Navbar";

// Custom color theme
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#4551be',
      light: '#6b77d4',
      dark: '#333d9e',
    },
    secondary: {
      main: '#00A8E8',
      light: '#4DC9FF',
      dark: '#0077B6',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: '#2196F3',
    },
    error: {
      main: '#F44336',
    },
    background: {
      default: '#f0f4f8',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

// Floating Animation Component
const FloatingAnimation = ({ children, delay = 0, duration = 2 }) => (
  <motion.div
    animate={{
      y: [0, -10, 0],
    }}
    transition={{
      duration: duration,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
      delay: delay
    }}
  >
    {children}
  </motion.div>
);

// Pulse Animation Component
const PulseAnimation = ({ children, scale = 1.05 }) => (
  <motion.div
    animate={{
      scale: [1, scale, 1],
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut"
    }}
  >
    {children}
  </motion.div>
);

// Shimmer Effect Component
const ShimmerEffect = ({ children }) => (
  <motion.div
    animate={{
      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
    }}
    transition={{
      duration: 3,
      repeat: Infinity,
      ease: "linear"
    }}
    style={{
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      backgroundSize: '200% 100%',
    }}
  >
    {children}
  </motion.div>
);

const KioskPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [isVisible, setIsVisible] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    setIsVisible(true);
    controls.start("visible");
  }, [controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: {
      y: 30,
      opacity: 0,
      scale: 0.95
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.43, 0.13, 0.23, 0.96]
      }
    }
  };

  const tabContentVariants = {
    hidden: {
      opacity: 0,
      x: 100,
      rotateY: -10
    },
    visible: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: [0.43, 0.13, 0.23, 0.96],
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    exit: {
      opacity: 0,
      x: -100,
      rotateY: 10,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  const gradientTextVariants = {
    hidden: {
      backgroundPosition: "0% 50%",
      opacity: 0
    },
    visible: {
      backgroundPosition: "100% 50%",
      opacity: 1,
      transition: {
        duration: 2,
        ease: "easeInOut",
        backgroundPosition: {
          duration: 3,
          repeat: Infinity,
          repeatType: "reverse"
        }
      }
    }
  };

  const features = [
    { icon: <TouchIcon />, title: 'Touchscreen Interface', description: 'User-friendly touch interface for easy interaction' },
    { icon: <CameraIcon />, title: 'Camera Integration', description: 'Selfie verification for attendance and security' },
    { icon: <PrintIcon />, title: 'Printer', description: 'Print training certificates and user IDs' },
    { icon: <ScannerIcon />, title: 'Document Scanner', description: 'Scan driving licenses and documents' },
    { icon: <AudioIcon />, title: 'Audio System', description: 'Multilingual audio guidance' },
    { icon: <QrIcon />, title: 'QR Code Support', description: 'QR code generation and scanning' },
  ];

  const benefits = [
    '24/7 Availability for driver training',
    'Reduces administrative workload by 60%',
    'Multi-language support for diverse workforce',
    'Real-time data synchronization',
    'Secure identity verification',
    'Automated certificate generation',
    'Remote monitoring capabilities',
    'Scalable for multiple terminals'
  ];

  const languages = [
    {
      code: 'ta',
      name: 'Tamil',
      native: 'தமிழ்',
      speakers: '80 million',
      region: 'Tamil Nadu, Sri Lanka',
      features: ['Native script support', 'Voice guidance', 'Cultural context']
    },
    {
      code: 'hi',
      name: 'Hindi',
      native: 'हिन्दी',
      speakers: '600 million',
      region: 'Northern India',
      features: ['Devanagari script', 'Audio instructions', 'Local regulations']
    },
    {
      code: 'en',
      name: 'English',
      native: 'English',
      speakers: '1.5 billion',
      region: 'Global',
      features: ['International standard', 'Technical terms', 'Default language']
    },
  ];

  const securityFeatures = [
    {
      title: 'Biometric Authentication',
      icon: <FingerprintIcon />,
      description: 'Fingerprint and facial recognition for secure access',
      level: 'High',
      benefits: ['Prevents unauthorized access', 'Eliminates password sharing', 'Real-time verification']
    },
    {
      title: 'Data Encryption',
      icon: <LockIcon />,
      description: 'End-to-end AES-256 encryption for all sensitive data',
      level: 'Critical',
      benefits: ['Secure data transmission', 'Protected storage', 'Compliance with regulations']
    },
    {
      title: 'Audit Trail',
      icon: <HistoryIcon />,
      description: 'Complete logging of all user activities and system events',
      level: 'Medium',
      benefits: ['Activity monitoring', 'Compliance tracking', 'Forensic analysis']
    },
    {
      title: 'Access Control',
      icon: <KeyIcon />,
      description: 'Role-based access control with granular permissions',
      level: 'High',
      benefits: ['Least privilege principle', 'Department segregation', 'Time-based access']
    },
    {
      title: 'Anti-Tampering',
      icon: <ShieldIcon />,
      description: 'Physical and software protection against tampering',
      level: 'Critical',
      benefits: ['Tamper-evident seals', 'Secure boot', 'Intrusion detection']
    },
    {
      title: 'Compliance',
      icon: <VerifiedIcon />,
      description: 'GDPR, ISO 27001 and local data protection compliance',
      level: 'High',
      benefits: ['Legal compliance', 'Industry standards', 'Regular audits']
    }
  ];

  const complianceStandards = [
    { standard: 'ISO 27001', status: 'Certified', level: 'Enterprise' },
    { standard: 'GDPR', status: 'Compliant', level: 'Full' },
    { standard: 'SOC 2 Type II', status: 'Audited', level: 'High' },
    { standard: 'PCI DSS', status: 'Compliant', level: 'Level 1' },
    { standard: 'HIPAA', status: 'Compliant', level: 'Full' },
    { standard: 'Local Regulations', status: 'Certified', level: 'State Level' }
  ];

  return (
    <>
      < Navbar />
      <ThemeProvider theme={customTheme}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            py: 8,
            bgcolor: '#f0f4f8',
            overflow: 'hidden',
            width: '100vw',
            minHeight: '100vh',
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw'
          }}>
            <Container maxWidth={false} sx={{
              px: { xs: 3, sm: 4, md: 6, lg: 8 },
            }}>
              {/* Header - LEFT ALIGNED */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 1,
                  ease: [0.43, 0.13, 0.23, 0.96],
                  delay: 0.2
                }}
              >
                <Box sx={{ textAlign: 'left', mt: 8 }}>

                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 1.2,
                      ease: "easeOut",
                      delay: 0.4
                    }}
                  >

                    {/* Logo + Title in Same Row */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: { xs: 2, md: 3 },
                        mb: 3,
                      }}
                    >
                      {/* Logo */}
                      <Box
                        component="img"
                        src="/images/kisok_logo.png"
                        alt="Kiosk Trainer Logo"
                        sx={{
                          width: { xs: 70, md: 100 },
                          height: { xs: 70, md: 100 },
                          objectFit: 'contain',
                          borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          p: 0.5,
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(0,0,0,0.1)',
                          flexShrink: 0,
                        }}
                      />

                      {/* Title */}
                      <Typography
                        variant="h1"
                        sx={{
                          fontWeight: 900,
                          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                          color: '#4551be',
                        }}
                      >
                        KIOSK TRAINER
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.6
                    }}
                  >
                    <FloatingAnimation delay={0.5}>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 4,
                          maxWidth: '900px',
                          color: '#475569',
                          lineHeight: 1.6,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          mx: 0,
                        }}
                      >
                        Standalone self-service terminals for driver induction training and access management
                      </Typography>
                    </FloatingAnimation>
                  </motion.div>
                </Box>
              </motion.div>

              {/* Animated Background Elements */}
              <motion.div
                animate={{
                  x: [0, 100, 0],
                  y: [0, -50, 0],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  position: 'absolute',
                  top: '10%',
                  left: '5%',
                  width: '300px',
                  height: '300px',
                  background: 'radial-gradient(circle, rgba(69,81,190,0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                  zIndex: 0,
                }}
              />
              <motion.div
                animate={{
                  x: [0, -100, 0],
                  y: [0, 50, 0],
                }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                  delay: 2
                }}
                style={{
                  position: 'absolute',
                  bottom: '10%',
                  right: '5%',
                  width: '400px',
                  height: '400px',
                  background: 'radial-gradient(circle, rgba(69,81,190,0.05) 0%, transparent 70%)',
                  borderRadius: '50%',
                  zIndex: 0,
                }}
              />

              {/* Tabs for Sections */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.7,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
              >
                <Paper sx={{
                  mb: { xs: 3, sm: 4, md: 6 },
                  borderRadius: { xs: 2, sm: 3 },
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  position: 'relative',
                  zIndex: 1,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,249,250,0.95))',
                  backdropFilter: 'blur(10px)',
                  width: '100%'
                }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    allowScrollButtonsMobile
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      width: '100%',
                      minHeight: { xs: 'auto', sm: 48 },
                      "& .MuiTabs-flexContainer": {
                        gap: {
                          xs: 0.5,
                          sm: 1,
                          md: 2,
                        },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                      },
                      '& .MuiTab-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        fontWeight: 600,
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1, sm: 1.5, md: 2 },
                        minWidth: { xs: 'auto', sm: 90, md: 'auto' },
                        minHeight: { xs: 44, sm: 'auto' },
                        flex: { xs: '0 1 auto', sm: 1 },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiTab-iconWrapper': {
                          marginBottom: { xs: '4px', sm: 0 },
                          marginRight: { xs: 0, sm: '8px' }
                        },

                        '&.Mui-selected': {
                          color: customTheme.palette.primary.main,
                          backgroundColor: 'rgba(69, 81, 190, 0.1)'
                        }
                      },
                      '& .MuiTabs-indicator': {
                        height: { xs: 2, sm: 3 },
                        borderRadius: '3px 3px 0 0',
                        backgroundColor: customTheme.palette.primary.main,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: { xs: 'none', sm: 'block' }
                      },
                      '& .MuiTabScrollButton-root': {
                        width: { xs: 32, sm: 40 },
                        '& .MuiSvgIcon-root': {
                          fontSize: { xs: 20, sm: 24 }
                        }
                      }
                    }}
                  >
                    {['Overview', 'Languages', 'Security', 'Training'].map((label, index) => (
                      <Tab
                        key={index}
                        icon={
                          <motion.div
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.5 }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: 0
                            }}
                          >
                            {index === 0 ? <TouchIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} /> :
                              index === 1 ? <GlobeIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} /> :
                                index === 2 ? <SecurityIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} /> :
                                  <TrainingIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />}
                          </motion.div>
                        }
                        iconPosition="start"
                        label={
                          <motion.span
                            initial={{ opacity: 0.8 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            style={{
                              fontSize: 'inherit',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {label}
                          </motion.span>
                        }
                        sx={{
                          textTransform: "none",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                          gap: {
                            xs: 0.5,
                            sm: 1,
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: {
                              xs: 18,
                              sm: 20,
                              md: 22,
                            },
                          },
                        }}
                      />
                    ))}
                  </Tabs>
                </Paper>
              </motion.div>

              {/* Animated Tab Content */}
              <Box sx={{ minHeight: '500px', position: 'relative', zIndex: 1 }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    variants={tabContentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{
                      duration: 0.6,
                      ease: [0.43, 0.13, 0.23, 0.96]
                    }}
                  >
                    {activeTab === 0 && (
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {/* About Section */}
                        <motion.div variants={itemVariants}>
                          <Card sx={{
                            mb: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.08)',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,249,250,0.9))',
                            backdropFilter: 'blur(10px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #4551be, #00A8E8)',
                            }
                          }}>
                            <CardHeader
                              title={
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <Typography variant="h3" fontWeight={700} color="#1a202c">
                                    About Kiosk Trainer
                                  </Typography>
                                </motion.div>
                              }
                              subheader="Self-service technology for modern terminal management"
                              sx={{ pb: 1 }}
                            />
                            <CardContent>
                              <Grid container spacing={4}>
                                <Grid item xs={12} lg={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: -50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.8,
                                      delay: 0.2,
                                      type: "spring",
                                      stiffness: 100
                                    }}
                                  >
                                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: '#4a5568' }}>
                                      Kiosk trainers are a popular form of self-service technology that allow drivers to interact with
                                      touchscreen interfaces to complete training and access information. These standalone trainers
                                      are customized for DP World terminal operations.
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: '#4a5568' }}>
                                      Designed to be user-friendly and intuitive, kiosk trainers facilitate various tasks including
                                      collecting driver information, providing induction training and creating user IDs for
                                      terminal safety and regulation compliance.
                                    </Typography>
                                    <motion.div
                                      whileHover={{ scale: 1.02 }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <Box sx={{ mt: 4, p: 3, bgcolor: '#f0f7ff', borderRadius: 2, borderLeft: '4px solid #4551be' }}>
                                        <Typography variant="h6" fontWeight={600} color="#2d3748" gutterBottom>
                                          Quick Facts:
                                        </Typography>
                                        <Grid container spacing={2}>
                                          {['24/7 Operation', 'Multi-language Support', 'Real-time Sync', 'Cloud Backup'].map((fact, idx) => (
                                            <Grid item xs={6} key={idx}>
                                              <motion.div
                                                whileHover={{ x: 5 }}
                                                transition={{ duration: 0.2 }}
                                              >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <motion.div
                                                    animate={{ rotate: [0, 360] }}
                                                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                                                  >
                                                    <CheckIcon fontSize="small" sx={{ color: '#4551be', mr: 1 }} />
                                                  </motion.div>
                                                  <Typography variant="body2" color="#4a5568">
                                                    {fact}
                                                  </Typography>
                                                </Box>
                                              </motion.div>
                                            </Grid>
                                          ))}
                                        </Grid>
                                      </Box>
                                    </motion.div>
                                  </motion.div>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.8,
                                      delay: 0.3,
                                      type: "spring",
                                      stiffness: 100
                                    }}
                                  >
                                    <Paper sx={{
                                      p: 4,
                                      bgcolor: '#f8f9fa',
                                      borderRadius: 2,
                                      border: '2px solid',
                                      borderColor: 'primary.light',
                                      height: '100%',
                                      position: 'relative',
                                      overflow: 'hidden'
                                    }}>
                                      <PulseAnimation scale={1.02}>
                                        <Typography variant="h5" gutterBottom fontWeight={600} color="#2d3748">
                                          Key Capabilities:
                                        </Typography>
                                      </PulseAnimation>
                                      <List>
                                        {['Collect driver information automatically',
                                          'Provide comprehensive induction training',
                                          'Generate unique user IDs for drivers',
                                          'Multi-language support for diverse workforce',
                                          'Real-time data synchronization',
                                          'Automated reporting and analytics'].map((item, index) => (
                                            <motion.div
                                              key={index}
                                              initial={{ opacity: 0, x: -20 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              transition={{
                                                duration: 0.5,
                                                delay: 0.4 + index * 0.1,
                                                type: "spring",
                                                stiffness: 200
                                              }}
                                            >
                                              <ListItem sx={{
                                                py: 1.5,
                                                '&:hover': {
                                                  bgcolor: 'rgba(69, 81, 190, 0.05)',
                                                  borderRadius: 1,
                                                  transform: 'translateX(10px)',
                                                  transition: 'all 0.3s ease'
                                                }
                                              }}>
                                                <ListItemIcon>
                                                  <motion.div
                                                    whileHover={{ scale: 1.5, rotate: 360 }}
                                                    transition={{ duration: 0.4 }}
                                                  >
                                                    <CheckIcon sx={{ color: customTheme.palette.primary.main }} />
                                                  </motion.div>
                                                </ListItemIcon>
                                                <ListItemText
                                                  primary={
                                                    <Typography variant="body1" color="#4a5568">
                                                      {item}
                                                    </Typography>
                                                  }
                                                />
                                              </ListItem>
                                            </motion.div>
                                          ))}
                                      </List>
                                    </Paper>
                                  </motion.div>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </motion.div>

                        {/* Features Grid */}
                        <motion.div variants={itemVariants}>
                          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, color: '#1a202c' }}>
                            Hardware Features
                          </Typography>
                        </motion.div>

                        <Grid container spacing={4} sx={{ mb: 8 }}>
                          {features.map((feature, index) => {
                            const colorSchemes = [
                              { bg: 'linear-gradient(135deg, #fff7ed, #ffedd5)', iconBg: '#f97316', chipColor: '#f97316' },
                              { bg: 'linear-gradient(135deg, #eff6ff, #dbeafe)', iconBg: '#3b82f6', chipColor: '#3b82f6' },
                              { bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', iconBg: '#10b981', chipColor: '#10b981' },
                              { bg: 'linear-gradient(135deg, #fefce8, #fef9c3)', iconBg: '#eab308', chipColor: '#eab308' },
                              { bg: 'linear-gradient(135deg, #faf5ff, #ede9fe)', iconBg: '#8b5cf6', chipColor: '#8b5cf6' },
                              { bg: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)', iconBg: '#14b8a6', chipColor: '#14b8a6' }
                            ];

                            const colors = colorSchemes[index % colorSchemes.length];

                            return (
                              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.8, rotateY: -90 }}
                                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                                  transition={{
                                    duration: 0.7,
                                    delay: index * 0.15,
                                    type: "spring",
                                    stiffness: 150,
                                    damping: 15
                                  }}
                                  whileHover="hover"
                                >
                                  <Card sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    background: colors.bg,
                                    border: '1px solid rgba(0,0,0,0.05)',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                    transition: "all 0.3s ease",
                                    '&:hover': {
                                      transform: "translateY(-10px) scale(1.03)",
                                      boxShadow: `0 20px 40px ${colors.iconBg}40`
                                    },
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      width: 120,
                                      height: 120,
                                      borderRadius: '50%',
                                      background: colors.iconBg,
                                      top: -30,
                                      right: -30,
                                      opacity: 0.15,
                                      filter: 'blur(50px)'
                                    }
                                  }}>
                                    <CardContent sx={{
                                      textAlign: 'center',
                                      p: 4,
                                      position: 'relative',
                                      zIndex: 1
                                    }}>
                                      <motion.div
                                        whileHover={{ rotate: 360 }}
                                        transition={{ duration: 0.6 }}
                                      >
                                        <Avatar sx={{
                                          bgcolor: 'white',
                                          width: 70,
                                          height: 70,
                                          mx: 'auto',
                                          mb: 3,
                                          fontSize: '2rem',
                                          color: colors.iconBg,
                                          boxShadow: `0 10px 20px ${colors.iconBg}30`
                                        }}>
                                          {feature.icon}
                                        </Avatar>
                                      </motion.div>

                                      <Typography variant="h6" gutterBottom fontWeight={700} sx={{ color: "#1e293b" }}>
                                        {feature.title}
                                      </Typography>

                                      <Typography variant="body2" sx={{ mb: 2, color: "#475569" }}>
                                        {feature.description}
                                      </Typography>

                                      <Chip
                                        label="Learn More"
                                        size="small"
                                        sx={{
                                          mt: 1,
                                          bgcolor: colors.iconBg,
                                          color: 'white',
                                          fontWeight: 500,
                                          cursor: 'pointer',
                                          '&:hover': {
                                            bgcolor: colors.iconBg,
                                            opacity: 0.9
                                          }
                                        }}
                                      />
                                    </CardContent>
                                  </Card>
                                </motion.div>
                              </Grid>
                            );
                          })}
                        </Grid>

                        {/* Benefits */}
                        <motion.div
                          initial={{ opacity: 0, y: 50, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{
                            duration: 1,
                            delay: 0.8,
                            type: "spring",
                            stiffness: 80,
                            damping: 15
                          }}
                        >
                          <Card
                            sx={{
                              borderRadius: 4,
                              mb: 5,
                              color: "#fff",
                              overflow: "hidden",
                              position: "relative",
                              background: "linear-gradient(135deg, #4551be, #6b77d4, #00A8E8)",
                              boxShadow: "0 20px 60px rgba(69, 81, 190, 0.5)",
                              transition: "all 0.4s ease",
                              "&:hover": {
                                transform: "scale(1.02)",
                                boxShadow: "0 30px 80px rgba(69, 81, 190, 0.7)"
                              }
                            }}
                          >
                            <ShimmerEffect>
                              <CardContent sx={{ p: { xs: 4, md: 6 }, position: "relative", zIndex: 1 }}>
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.9 }}
                                >
                                  <Typography
                                    variant="h3"
                                    gutterBottom
                                    fontWeight={700}
                                    sx={{
                                      mb: 4,
                                      background: "linear-gradient(90deg, #fff, #c7d2fe)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent"
                                    }}
                                  >
                                    Benefits of Kiosk Implementation
                                  </Typography>
                                </motion.div>

                                <Grid container spacing={3}>
                                  {benefits.map((benefit, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                      <motion.div
                                        initial={{ opacity: 0, x: -30, scale: 0.8 }}
                                        animate={{ opacity: 1, x: 0, scale: 1 }}
                                        transition={{
                                          duration: 0.6,
                                          delay: 1 + index * 0.05,
                                          type: "spring",
                                          stiffness: 200
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 2,
                                            p: 2,
                                            borderRadius: 3,
                                            backdropFilter: "blur(10px)",
                                            background: "rgba(255,255,255,0.1)",
                                            border: "1px solid rgba(255,255,255,0.2)",
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                            "&:hover": {
                                              background: "rgba(255,255,255,0.2)",
                                              transform: "translateX(10px) scale(1.05)",
                                              boxShadow: "0 10px 25px rgba(0,0,0,0.3)"
                                            }
                                          }}
                                        >
                                          <motion.div
                                            whileHover={{ scale: 1.3, rotate: 360 }}
                                            whileTap={{ scale: 0.9 }}
                                            transition={{ duration: 0.3 }}
                                          >
                                            <CheckIcon
                                              sx={{
                                                mr: 2,
                                                color: "#4ade80",
                                                fontSize: 28,
                                                filter: "drop-shadow(0 0 6px #4ade80)"
                                              }}
                                            />
                                          </motion.div>

                                          <Typography sx={{ fontSize: "0.95rem" }}>
                                            {benefit}
                                          </Typography>
                                        </Box>
                                      </motion.div>
                                    </Grid>
                                  ))}
                                </Grid>
                              </CardContent>
                            </ShimmerEffect>
                            <Box
                              sx={{
                                position: "absolute",
                                width: 300,
                                height: 300,
                                background: "rgba(228, 176, 176, 0.1)",
                                borderRadius: "50%",
                                top: -100,
                                right: -100,
                                filter: "blur(100px)"
                              }}
                            />
                          </Card>
                        </motion.div>

                      </motion.div>
                    )}

                    {activeTab === 1 && (
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {/* Languages Section */}
                        <motion.div variants={itemVariants}>
                          <Card sx={{
                            mb: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.08)',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,249,250,0.95))',
                            backdropFilter: 'blur(10px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #4CAF50, #2196F3)',
                            }
                          }}>
                            <CardHeader
                              title={
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <Typography variant="h3" fontWeight={700} color="#1a202c">
                                    Multi-Language Support
                                  </Typography>
                                </motion.div>
                              }
                              subheader="Comprehensive language solutions for diverse workforce training"
                              sx={{ pb: 1 }}
                            />
                            <CardContent>
                              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: '#4a5568', mb: 4 }}>
                                Our kiosk system supports multiple languages to ensure every driver receives training in their preferred language,
                                improving comprehension and compliance. The system automatically detects language preferences and provides localized content.
                              </Typography>

                              <Grid container spacing={4} sx={{ mb: 4 }}>
                                {languages.map((language, index) => (
                                  <Grid item xs={12} md={4} key={index}>
                                    <motion.div
                                      initial={{ opacity: 0, y: 30 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: index * 0.2 }}
                                      whileHover={{ scale: 1.05, y: -5 }}
                                    >
                                      <Card sx={{
                                        height: '100%',
                                        borderRadius: 3,
                                        border: '1px solid #e2e8f0',
                                        background: index === 0 ? 'linear-gradient(135deg, #f0f9ff, #e0f2fe)' :
                                          index === 1 ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)' :
                                            'linear-gradient(135deg, #fefce8, #fef9c3)'
                                      }}>
                                        <CardContent sx={{ p: 3 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{
                                              bgcolor: index === 0 ? '#0369a1' : index === 1 ? '#15803d' : '#ca8a04',
                                              mr: 2,
                                              width: 50,
                                              height: 50
                                            }}>
                                              <TranslateIcon />
                                            </Avatar>
                                            <Box>
                                              <Typography variant="h5" fontWeight={600} color="#1a202c">
                                                {language.name}
                                              </Typography>
                                              <Typography variant="h4" color="text.secondary">
                                                {language.native}
                                              </Typography>
                                            </Box>
                                          </Box>

                                          <Stack spacing={1} sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <VoiceIcon fontSize="small" sx={{ mr: 1, color: '#64748b' }} />
                                              <Typography variant="body2" color="#64748b">
                                                Speakers: {language.speakers}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <GlobeIcon fontSize="small" sx={{ mr: 1, color: '#64748b' }} />
                                              <Typography variant="body2" color="#64748b">
                                                Region: {language.region}
                                              </Typography>
                                            </Box>
                                          </Stack>

                                          <Divider sx={{ my: 2 }} />

                                          <Typography variant="subtitle2" fontWeight={600} color="#1a202c" gutterBottom>
                                            Features:
                                          </Typography>
                                          <Stack spacing={1}>
                                            {language.features.map((feature, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckIcon fontSize="small" sx={{ mr: 1, color: '#10b981' }} />
                                                <Typography variant="body2" color="#64748b">
                                                  {feature}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </motion.div>
                                  </Grid>
                                ))}
                              </Grid>

                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                              >
                                <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                                  <Typography variant="h6" gutterBottom>
                                    Additional Language Features
                                  </Typography>
                                  <Typography>
                                    • Real-time translation for training materials
                                    • Voice recognition for hands-free operation
                                    • Text-to-speech for visually impaired users
                                    • Cultural context adaptation in training content
                                  </Typography>
                                </Alert>
                              </motion.div>

                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 }}
                                  >
                                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                                      <Typography variant="h6" gutterBottom fontWeight={600} color="#1a202c">
                                        Language Benefits
                                      </Typography>
                                      <List>
                                        {[
                                          'Improved comprehension and retention',
                                          'Reduced training time by 40%',
                                          'Increased safety compliance',
                                          'Better engagement and participation',
                                          'Cultural sensitivity and inclusivity'
                                        ].map((item, idx) => (
                                          <ListItem key={idx} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                              <CheckIcon color="success" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={item} />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Paper>
                                  </motion.div>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.8 }}
                                  >
                                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                                      <Typography variant="h6" gutterBottom fontWeight={600} color="#1a202c">
                                        Implementation
                                      </Typography>
                                      <List>
                                        {[
                                          'Automatic language detection',
                                          'Seamless switching between languages',
                                          'Regular language pack updates',
                                          'Local dialect support',
                                          'Offline language availability'
                                        ].map((item, idx) => (
                                          <ListItem key={idx} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                              <CheckIcon color="success" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={item} />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Paper>
                                  </motion.div>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </motion.div>
                    )}

                    {activeTab === 2 && (
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {/* Security Section */}
                        <motion.div variants={itemVariants}>
                          <Card sx={{
                            mb: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.08)',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,249,250,0.95))',
                            backdropFilter: 'blur(10px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #ef4444, #3b82f6)',
                            }
                          }}>
                            <CardHeader
                              title={
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <Typography variant="h3" fontWeight={700} color="#1a202c">
                                    Security Features
                                  </Typography>
                                </motion.div>
                              }
                              subheader="Enterprise-grade security for your terminal operations"
                              sx={{ pb: 1 }}
                            />
                            <CardContent>
                              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: '#4a5568', mb: 4 }}>
                                Our kiosk systems implement multi-layered security protocols to ensure data protection, prevent unauthorized access,
                                and maintain compliance with international security standards.
                              </Typography>

                              {/* Security Features Grid */}
                              <Grid container spacing={3} sx={{ mb: 4 }}>
                                {securityFeatures.map((feature, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={index}>
                                    <motion.div
                                      initial={{ opacity: 0, y: 30 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: index * 0.1 }}
                                      whileHover={{ scale: 1.03, y: -5 }}
                                    >
                                      <Card sx={{
                                        height: '100%',
                                        borderRadius: 2,
                                        border: '1px solid #e2e8f0',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          borderColor: feature.level === 'Critical' ? '#ef4444' :
                                            feature.level === 'High' ? '#f59e0b' : '#3b82f6',
                                          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                                        }
                                      }}>
                                        <CardContent sx={{ p: 3 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{
                                              bgcolor: feature.level === 'Critical' ? '#fef2f2' :
                                                feature.level === 'High' ? '#fffbeb' : '#eff6ff',
                                              color: feature.level === 'Critical' ? '#dc2626' :
                                                feature.level === 'High' ? '#d97706' : '#2563eb',
                                              mr: 2,
                                              width: 50,
                                              height: 50
                                            }}>
                                              {feature.icon}
                                            </Avatar>
                                            <Box>
                                              <Typography variant="h6" fontWeight={600} color="#1a202c">
                                                {feature.title}
                                              </Typography>
                                              <Chip
                                                label={feature.level}
                                                size="small"
                                                sx={{
                                                  mt: 0.5,
                                                  bgcolor: feature.level === 'Critical' ? '#fef2f2' :
                                                    feature.level === 'High' ? '#fffbeb' : '#eff6ff',
                                                  color: feature.level === 'Critical' ? '#dc2626' :
                                                    feature.level === 'High' ? '#d97706' : '#2563eb',
                                                  fontWeight: 600,
                                                  fontSize: '0.7rem'
                                                }}
                                              />
                                            </Box>
                                          </Box>

                                          <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                                            {feature.description}
                                          </Typography>

                                          <Typography variant="subtitle2" fontWeight={600} color="#1a202c" gutterBottom>
                                            Benefits:
                                          </Typography>
                                          <Stack spacing={1}>
                                            {feature.benefits.map((benefit, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckIcon fontSize="small" sx={{ mr: 1, color: '#10b981' }} />
                                                <Typography variant="body2" color="#64748b">
                                                  {benefit}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </motion.div>
                                  </Grid>
                                ))}
                              </Grid>

                              {/* Compliance Standards */}
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                              >
                                <Paper sx={{ p: 3, borderRadius: 2, mb: 4, bgcolor: '#f8f9fa' }}>
                                  <Typography variant="h5" gutterBottom fontWeight={600} color="#1a202c">
                                    Compliance & Certifications
                                  </Typography>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell><strong>Standard</strong></TableCell>
                                          <TableCell><strong>Status</strong></TableCell>
                                          <TableCell><strong>Level</strong></TableCell>
                                          <TableCell><strong>Validity</strong></TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {complianceStandards.map((standard, index) => (
                                          <TableRow key={index} hover>
                                            <TableCell>{standard.standard}</TableCell>
                                            <TableCell>
                                              <Chip
                                                label={standard.status}
                                                size="small"
                                                color={standard.status === 'Certified' ? 'success' : 'primary'}
                                              />
                                            </TableCell>
                                            <TableCell>{standard.level}</TableCell>
                                            <TableCell>
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                                                <Typography variant="body2">Valid until 2025</Typography>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Paper>
                              </motion.div>

                              {/* Security Statistics */}
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7 }}
                                  >
                                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                                      <Typography variant="h6" gutterBottom fontWeight={600} color="#1a202c">
                                        Security Metrics
                                      </Typography>
                                      <Stack spacing={2}>
                                        {[
                                          { label: 'Data Encryption', value: 100, color: '#10b981' },
                                          { label: 'Access Control', value: 95, color: '#3b82f6' },
                                          { label: 'Audit Compliance', value: 98, color: '#8b5cf6' },
                                          { label: 'Incident Response', value: 90, color: '#f59e0b' }
                                        ].map((metric, idx) => (
                                          <Box key={idx}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                              <Typography variant="body2" color="#64748b">{metric.label}</Typography>
                                              <Typography variant="body2" fontWeight={600} color="#1a202c">{metric.value}%</Typography>
                                            </Box>
                                            <LinearProgress
                                              variant="determinate"
                                              value={metric.value}
                                              sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: '#e2e8f0',
                                                '& .MuiLinearProgress-bar': {
                                                  backgroundColor: metric.color,
                                                  borderRadius: 4
                                                }
                                              }}
                                            />
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Paper>
                                  </motion.div>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.8 }}
                                  >
                                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                                      <Typography variant="h6" gutterBottom fontWeight={600} color="#1a202c">
                                        Security Benefits
                                      </Typography>
                                      <List>
                                        {[
                                          'Zero security breaches since deployment',
                                          '99.9% system uptime guarantee',
                                          'Real-time threat monitoring',
                                          'Automated security updates',
                                          '24/7 security operations center',
                                          'Regular penetration testing'
                                        ].map((item, idx) => (
                                          <ListItem key={idx} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                              <ShieldIcon fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                              primary={item}
                                              primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Paper>
                                  </motion.div>
                                </Grid>
                              </Grid>

                              {/* Security Alert */}
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.9 }}
                              >
                                <Alert
                                  severity="warning"
                                  sx={{
                                    mt: 4,
                                    borderRadius: 2,
                                    border: '1px solid #fbbf24'
                                  }}
                                >
                                  <Typography variant="h6" gutterBottom>
                                    Security Best Practices
                                  </Typography>
                                  <Typography>
                                    • Regular security audits every quarter
                                    • Automatic security patch updates
                                    • Multi-factor authentication for administrators
                                    • Encrypted data transmission and storage
                                    • Regular security awareness training for staff
                                  </Typography>
                                </Alert>
                              </motion.div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </motion.div>
                    )}

                    {activeTab === 3 && (
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {/* Training Section */}
                        <motion.div variants={itemVariants}>
                          <Card sx={{
                            mb: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.08)',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,249,250,0.95))',
                            backdropFilter: 'blur(10px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #8b5cf6, #ec4899)',
                            }
                          }}>
                            <CardHeader
                              title={
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  <Typography variant="h3" fontWeight={700} color="#1a202c">
                                    Training Program
                                  </Typography>
                                </motion.div>
                              }
                              subheader="Comprehensive driver training and certification"
                              sx={{ pb: 1 }}
                            />
                            <CardContent>
                              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem', color: '#4a5568', mb: 4 }}>
                                Our training program provides comprehensive safety and operational training for terminal drivers,
                                ensuring compliance with international standards and local regulations.
                              </Typography>
                              <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                                <Typography>
                                  Training content is regularly updated to reflect the latest safety regulations and best practices.
                                </Typography>
                              </Alert>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </Box>
         

            </Container>
          </Box>
        </motion.div>
      </ThemeProvider>
           <HomeFooter />
    </>
  );
};

export default KioskPage;